import MDButton from "components/MDButton";
import { ComponentProps } from "react";
import { draftColors, draftTypography } from "modules/proposals/draft/draftDesignTokens";

type MDButtonProps = ComponentProps<typeof MDButton>;

type UiButtonVariant =
  | "primary"
  | "primary-blue"
  | "primary-green"
  | "primary-grey"
  | "primary-red"
  | "outlined"
  | "outlined-small"
  | "action"
  | "action-blue"
  | "action-green";

type UiButtonProps = Omit<MDButtonProps, "variant"> & {
  uiVariant?: UiButtonVariant;
};

const baseOutlinedSx = {
  backgroundColor: `${draftColors.surface} !important`,
  borderColor: `${draftColors.border} !important`,
  color: `${draftColors.textAction} !important`,
  borderRadius: "8px",
  textTransform: "none",
  "&:hover": { backgroundColor: `${draftColors.surface} !important` },
} as const;

const variantStyles: Record<UiButtonVariant, Record<string, unknown>> = {
  primary: {
    ...baseOutlinedSx,
    backgroundColor: `${draftColors.textAction} !important`,
    color: `${draftColors.surface} !important`,
    px: "12px",
    py: "6px",
    minHeight: "32px",
    fontSize: draftTypography.label12Bold.fontSize,
    fontWeight: draftTypography.label12Bold.fontWeight,
    lineHeight: draftTypography.label12Bold.lineHeight,
    boxShadow: "none",
    "&:hover": { backgroundColor: `${draftColors.textAction} !important` },
  },
  "primary-blue": {
    backgroundColor: `${draftColors.blue} !important`,
    color: `${draftColors.surface} !important`,
    px: "7px",
    py: "4px",
    minHeight: "28px",
    borderRadius: "8px",
    fontSize: draftTypography.label12Bold.fontSize,
    fontWeight: draftTypography.label12Bold.fontWeight,
    lineHeight: draftTypography.label12Bold.lineHeight,
    textTransform: "none",
    boxShadow: "0px 1px 3px rgba(0,0,0,0.12) !important",
    "&:hover": { backgroundColor: `${draftColors.blue} !important` },
  },
  "primary-green": {
    backgroundColor: `${draftColors.green} !important`,
    color: `${draftColors.surface} !important`,
    px: "7px",
    py: "4px",
    minHeight: "28px",
    borderRadius: "8px",
    fontSize: draftTypography.label12Bold.fontSize,
    fontWeight: draftTypography.label12Bold.fontWeight,
    lineHeight: draftTypography.label12Bold.lineHeight,
    textTransform: "none",
    boxShadow: "0px 1px 3px rgba(0,0,0,0.12) !important",
    "&:hover": { backgroundColor: `${draftColors.green} !important` },
  },
  "primary-grey": {
    backgroundColor: `${draftColors.textAction} !important`,
    color: `${draftColors.surface} !important`,
    px: "7px",
    py: "4px",
    minHeight: "28px",
    borderRadius: "8px",
    fontSize: draftTypography.label12Bold.fontSize,
    fontWeight: draftTypography.label12Bold.fontWeight,
    lineHeight: draftTypography.label12Bold.lineHeight,
    textTransform: "none",
    boxShadow: "0px 1px 3px rgba(0,0,0,0.12) !important",
    "&:hover": { backgroundColor: `${draftColors.textAction} !important` },
  },
  "primary-red": {
    backgroundColor: `${draftColors.red} !important`,
    color: `${draftColors.surface} !important`,
    px: "7px",
    py: "4px",
    minHeight: "28px",
    borderRadius: "8px",
    fontSize: draftTypography.label12Bold.fontSize,
    fontWeight: draftTypography.label12Bold.fontWeight,
    lineHeight: draftTypography.label12Bold.lineHeight,
    textTransform: "none",
    boxShadow: "0px 1px 3px rgba(0,0,0,0.12) !important",
    "&:hover": { backgroundColor: `${draftColors.red} !important` },
  },
  outlined: {
    ...baseOutlinedSx,
    px: "7px",
    py: "4px",
    minHeight: "28px",
    fontSize: draftTypography.label12Bold.fontSize,
    fontWeight: draftTypography.label12Bold.fontWeight,
    lineHeight: draftTypography.label12Bold.lineHeight,
    boxShadow: "0px 1px 3px rgba(0,0,0,0.12) !important",
  },
  "outlined-small": {
    ...baseOutlinedSx,
    px: "8px",
    py: "2px",
    minHeight: "24px",
    fontSize: draftTypography.label12Semi.fontSize,
    fontWeight: draftTypography.label12Semi.fontWeight,
    lineHeight: draftTypography.label12Semi.lineHeight,
    boxShadow: "none !important",
  },
  action: {
    px: "18px",
    py: "10px",
    minHeight: "40px",
    borderRadius: "10px",
    fontSize: draftTypography.label12Bold.fontSize,
    fontWeight: 700,
    lineHeight: draftTypography.label12Bold.lineHeight,
    textTransform: "none",
    boxShadow: "0px 1px 3px rgba(0,0,0,0.12) !important",
  },
  "action-blue": {
    backgroundColor: `${draftColors.blue} !important`,
    color: `${draftColors.surface} !important`,
    px: "18px",
    py: "10px",
    minHeight: "40px",
    borderRadius: "10px",
    fontSize: draftTypography.label12Bold.fontSize,
    fontWeight: 700,
    lineHeight: draftTypography.label12Bold.lineHeight,
    textTransform: "none",
    boxShadow: "0px 1px 3px rgba(0,0,0,0.12) !important",
    "&:hover": { backgroundColor: `${draftColors.blue} !important` },
  },
  "action-green": {
    backgroundColor: `${draftColors.green} !important`,
    color: `${draftColors.surface} !important`,
    px: "18px",
    py: "10px",
    minHeight: "40px",
    borderRadius: "10px",
    fontSize: draftTypography.label12Bold.fontSize,
    fontWeight: 700,
    lineHeight: draftTypography.label12Bold.lineHeight,
    textTransform: "none",
    boxShadow: "0px 1px 3px rgba(0,0,0,0.12) !important",
    "&:hover": { backgroundColor: `${draftColors.green} !important` },
  },
};

const muiVariantMap: Record<UiButtonVariant, "contained" | "outlined"> = {
  primary: "contained",
  "primary-blue": "contained",
  "primary-green": "contained",
  "primary-grey": "contained",
  "primary-red": "contained",
  outlined: "outlined",
  "outlined-small": "outlined",
  action: "contained",
  "action-blue": "contained",
  "action-green": "contained",
};

export default function UiButton({ uiVariant = "outlined", sx, ...rest }: UiButtonProps) {
  return (
    <MDButton
      size="small"
      variant={muiVariantMap[uiVariant]}
      sx={{ ...variantStyles[uiVariant], ...sx }}
      {...rest}
    />
  );
}
